import React, { ChangeEvent, Fragment, useEffect, useState } from 'react';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import {
  Box,
  Button,
  Card,
  CardHeader,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@emotion/styled';
import { withStyles } from '@mui/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { useForm } from 'react-hook-form';
import {
  getAllDefineLeaves,
  deleteDefineLeaves,
  searchDefineLeave
} from 'src/services/DefineLeaveService';
import ToggleModal from './ToggleModal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/hooks/hooks';
import { DefineLeavesInformation } from 'src/models/leave';
import { LeaveType } from './enum/DefineLeaves';

const applyPagination = (
  employees: any[],
  page: number,
  limit: number
): any[] => {
  return employees.slice(page * limit, page * limit + limit);
};

const DefineLeavesView = () => {
  const dispatch = useDispatch();

  const { isLoading } = useAppSelector((state) => state.appReducer);
  var initalList: DefineLeavesInformation[] = [];
  const [defineLeaves, setDefineLeave] = useState(initalList);
  const [segments, setSegments] = useState<any>([]);
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);

  const paginatedDefineLeaves = applyPagination(
    defineLeaves,
    page,
    limit
  );

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  const getDefineLeaveData = () => {
    dispatch(AppActions.showLoader());
    getAllDefineLeaves().then((res) => {
      setDefineLeave(res.data.data);
      dispatch(AppActions.hideLoader());
    });
  };

  useEffect(() => {
    getDefineLeaveData();
  }, []);

  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  const [searchKeywords, setSearchKeywords] = useState('');

  //when user started to type the name in the text field
  const employmentTypeChange = (event) => {
    setSearchKeywords(event.target.value);
  };

  const searchGroupsAll = async (keyword: string) => {
    dispatch(AppActions.showLoader());
    const result = await searchDefineLeave(keyword.toUpperCase());
    setDefineLeave(result?.data.data.reverse());
    dispatch(AppActions.hideLoader());
  };

  useEffect(() => {
    if (searchKeywords.length > 0) {
      searchGroupsAll(searchKeywords);
    }
    if (searchKeywords.length === 0) {
      getDefineLeaveData();
    }
  }, [searchKeywords]);

  const [openDefineLeaves, setOpenDefineLeaves] = useState(false);
  const [defineLeaveEdit, setDefineLeaveEdit] = useState(false);
  const [editDataDefineLeave, setEditDataDefineLeave] = useState<any>({});


  const [defineLeaveDeleteID, setDefineLeaveDeleteID] = useState('');
  const [sortEmploymentType, setSortEmploymentType] = useState('asc');
  const [sortLeaveType, setSortLeaveType] = useState('asc');

  useEffect(() => {
    if (defineLeaves?.length > 0 && sortEmploymentType === 'asc') {
      setDefineLeave(defineLeaves.sort((a, b) => a.employmentType?.localeCompare(b.employmentType)));
    } else if (defineLeaves?.length > 0 && sortEmploymentType === 'desc') {
      setDefineLeave(defineLeaves.sort((a, b) => b.employmentType?.localeCompare(a.employmentType)));
    }
  }, [defineLeaves, sortEmploymentType]);

  useEffect(() => {
    if (defineLeaves?.length > 0 && sortLeaveType === 'asc') {
      setDefineLeave(defineLeaves.sort((a, b) => a.employmentType?.localeCompare(b.employmentType)));
    } else if (defineLeaves?.length > 0 && sortLeaveType === 'desc') {
      setDefineLeave(defineLeaves.sort((a, b) => b.employmentType?.localeCompare(a.employmentType)));
    }
  }, [defineLeaves, sortLeaveType]);

  const toggleDefineLeavesAdd = () => {
    setOpenDefineLeaves(!openDefineLeaves);
  };

  const [
    openConfirmationDefineLeaveDelete,
    setOpenConfirmationDefineLeaveDelete
  ] = useState(false);

  const toggleConfirmationDefineLeaveDelete = () => {
    setOpenConfirmationDefineLeaveDelete(!openConfirmationDefineLeaveDelete);
  };

  const deleteDefineLeaveFunc = () => {
    deleteDefineLeaves(defineLeaveDeleteID)
      .then(() => {
        dispatch(
          AppActions.toggleSnackBarOpen({
            message: 'You have deleted the leave type successfully.',
            severity: 'success'
          })
        );
        getDefineLeaveData();
        toggleConfirmationDefineLeaveDelete();
      })
      .catch((error) => {
        if(error?.response?.data?.statusCode === 400){
          dispatch(
                AppActions.toggleSnackBarOpen({
                  message: error?.response?.data?.message,
                  severity: 'error'
                })
              );
              toggleConfirmationDefineLeaveDelete();
        }else {
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: 'Unable to delete defined leave',
              severity: 'error'
            })
          );
        }
      });
  };

  return (
    <Fragment>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Admin Configurations / <span style={{ fontSize: '14px', fontWeight: 500 }}>Define Leaves</span>
          </Typography> 
          <Typography variant="subtitle2"></Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => {
              setDefineLeaveEdit(false);
              toggleDefineLeavesAdd();
            }}
            sx={{
              mt: { xs: 2, md: 0 },
              fontSize: '11px',
              fontWeight: 'normal'
            }}
            variant="contained"
            startIcon={<AddTwoToneIcon fontSize="small" />}
            style={{ height: '1.75rem' }}
          >
            Create New
          </Button>
        </Grid>
      </Grid>
      <Card>
        <Grid
          container
          style={{
            justifyContent: 'space-between',
            padding: '1rem'
          }}
          spacing={2}
        >
          <Grid item xs={6}>
            <TextField
              sx={{ width: 320, mr: 2 }}
              size="small"
              placeholder="Employment Type and Leave Type"
              onChange={(e) => employmentTypeChange(e)}
              InputProps={{
                style: { fontSize: '12px', height: '1.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: '#d3d3d3' }}
                      style={{ fontSize: '15px' }}
                    />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
        <TableContainer>
          <Table style={{ borderCollapse: 'separate' }} size="small">
            <TableHead sx={{ backgroundColor: '#e6e9ff' }}>
              <CustomTableRow sx={{ py: 10 }}>
                <TableHeaderCell 
                  content="Employment Type" 
                  smallFont 
                  sort
                  setSortType={setSortEmploymentType}
                  sortType={sortEmploymentType}
                  />
                <TableHeaderCell 
                  content="Leave Type" 
                  smallFont 
                  // sort
                  // setSortType={setSortLeaveType}
                  // sortType={sortLeaveType}
                  />
                <TableHeaderCell content="Gender" smallFont />
                <TableHeaderCell content="No of Days" smallFont />
                <TableHeaderCell content="HR Approval" smallFont />
                <TableHeaderCell content="Renew Period" smallFont />
                <TableHeaderCell content="Actions" smallFont sticky />
              </CustomTableRow>
            </TableHead>
            <TableBody>
              {paginatedDefineLeaves &&
                paginatedDefineLeaves?.length > 0 &&
                paginatedDefineLeaves.map((listItem) => {
                  return (
                    <TableRow hover>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {listItem?.employmentType}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {listItem?.typeName}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {listItem?.gender}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {(listItem?.typeName === LeaveType.LIEU_LEAVE) || 
                          (listItem?.typeName === LeaveType.NO_PAY_LEAVE) || 
                          (listItem?.typeName === LeaveType.WORK_FROM_HOME) ||
                          (listItem?.typeName === LeaveType.CARRY_FORWARD_LEAVE)
                          ? 'N/A' : listItem?.noOfDays}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {listItem?.hrApproved ? 'Yes' : 'No'}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body2"
                          gutterBottom
                          noWrap
                        >
                          {listItem?.renewPeriod  == 'NA' ? 'N/A' : listItem?.renewPeriod}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell sticky>
                        <div
                          style={{
                            background: 'white',
                            marginLeft: 'auto'
                          }}
                        >
                          <EditIcon
                            onClick={() => {
                              setDefineLeaveEdit(true);
                              setEditDataDefineLeave(listItem);
                              toggleDefineLeavesAdd();
                            }}
                            style={{
                              fontSize: '17px',
                              cursor: 'pointer'
                            }}
                          />
                          <DeleteOutlineIcon
                            onClick={() => {
                              setDefineLeaveDeleteID(listItem.id);
                              toggleConfirmationDefineLeaveDelete();
                            }}
                            style={{
                              fontSize: '17px',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                      </TableColumnCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        {paginatedDefineLeaves?.length === 0 && (
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: '1rem',
              textAlign: 'center'
            }}
            variant="body2"
          >
            No Records Found
          </Typography>
        )}
        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={defineLeaves?.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
      <ToggleModal
        open={openDefineLeaves}
        toggle={toggleDefineLeavesAdd}
        isEdit={defineLeaveEdit}
        getData={getDefineLeaveData}
        data={editDataDefineLeave}
        length={paginatedDefineLeaves.length}
        segments={segments}
      />
      <ConfirmationModal
        isLoading={isLoading}
        open={openConfirmationDefineLeaveDelete}
        toggle={toggleConfirmationDefineLeaveDelete}
        message={`Are you sure you want to delete this leave type?`}
        onConfirm={() => deleteDefineLeaveFunc()}
        noToggle={toggleConfirmationDefineLeaveDelete}
      />
    </Fragment>
  );
};

export default DefineLeavesView;
